import React from 'react'
import { useSelector } from 'react-redux'
import {
  forEach,
  isPlainObject,
  isArray,
  camelCase,
  snakeCase,
  kebabCase,
} from 'lodash'
import nanoid from 'nanoid'
import sha256 from 'crypto-js/sha256'

import { useParams } from 'react-router-dom'
import { triggerGtmEvent } from 'App/GTM'

export const strToKebabCase = (str) => {
  return kebabCase(str)
}

export const objectKeysToCamelCase = (obj) => {
  return convertObjectKeys(obj, camelCase)
}

export const objectKeysToSnakeCase = (obj) => {
  return convertObjectKeys(obj, snakeCase)
}

// See: https://stackoverflow.com/a/41991812
const convertObjectKeys = (obj, conversionFn) => {
  let newObject = {}
  let newArray = []
  forEach(obj, (value, key) => {
    if (isPlainObject(value) || isArray(value)) {
      value = convertObjectKeys(value, conversionFn)
    }

    if (isArray(obj)) {
      newArray.push(value)
    } else {
      newObject[conversionFn(key)] = value
    }
  })
  if (isArray(obj)) return newArray
  else return newObject
}

export const openLink = (
  url,
  target = '_blank',
  rel = 'noopener noreferrer'
) => {
  const anchor = document.createElement('a')
  anchor.href = url
  if (rel) anchor.rel = rel
  anchor.target = target
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

export const convertToUrlCase = (str) => {
  return kebabCase(str)
}

export const useProductShortCodeFromParams = () => {
  const { productShortCode } = useParams()
  return snakeCase(productShortCode)
}

export const useActivePaidProductNames = (str) => {
  const products = str.split('\n')
  return products
    .map((product) => product.replace(/<\/?p[^>]*>/g, ''))
    .filter(Boolean)
}

export const getExperimentData = (experiments, original, variant) => {
  const experimentsToArray = experiments ? Object.values(experiments) : []
  return experimentsToArray?.find(
    (e) => e.original === original && e.variant === variant
  )
}

export const sessionId = nanoid()

export const useMembershipMessage = (
  membershipName,
  defaultAmount,
  discountedAmount,
  membershipPeriod,
  currencySymbol
) => {
  const { formatMessage } = useSelector((state) => state.hyperion.intl)

  if (
    !(
      membershipName &&
      defaultAmount &&
      discountedAmount &&
      membershipPeriod &&
      currencySymbol
    )
  )
    return ''

  let id = 'journey.spend.review-spend.summary.price-message'

  if (
    discountedAmount &&
    defaultAmount &&
    parseFloat(defaultAmount) !== parseFloat(discountedAmount) &&
    membershipPeriod !== 'year'
  ) {
    id = 'journey.spend.review-spend.summary.discount-price-message'
  }

  return formatMessage(
    { id: id },
    {
      b: (chunks) => <b key={'membership-b'}>{chunks}</b>,
      s: (chunks) => <s key={'membership-s'}>{chunks}</s>,
      membershipName: membershipName,
      defaultAmount: defaultAmount,
      discountedAmount: discountedAmount,
      membershipPeriod: membershipPeriod,
      currencySymbol: currencySymbol,
    }
  )
}

const removeSpecialCharsAndSpaces = (inputString) => {
  return typeof inputString === 'string'
    ? inputString.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    : inputString
}

export const randomizeOptions = (options) => {
  const result = [...options]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export const hashDataLayerValues = (userData) => {
  if (!userData) return
  userData['phone_number'] = '44' + userData['phone_number']?.slice(1)
  userData['phone_number_with_plus'] =
    '+44' + userData['phone_number']?.slice(1)
  return Object.keys(userData)
    .map((key) => {
      return key !== 'zipcode' &&
        key !== 'state' &&
        key !== 'userId' &&
        key !== 'town' &&
        key !== 'purchaseData'
        ? userData[key]
          ? {
              [key]: sha256(
                removeSpecialCharsAndSpaces(userData[key])
              ).toString(),
            }
          : { [key]: '' }
        : { [key]: userData[key] }
    })
    .reduce((acc, curr) => {
      return {
        ...acc,
        ...curr,
      }
    }, {})
}

export const triggerNavigationGtmEvent = ({
  eventName,
  cardValue = undefined,
  itemValue = undefined,
}) => {
  triggerGtmEvent({
    event: 'dlvPushNavigationCommon',
    eventName: eventName,
    navigationPushCommonValue: {
      card: cardValue,
      item: itemValue,
    },
  })
}
